import React, { useEffect, useRef, useState } from 'react';
import { API } from '../App';
import {
    Box, Stack, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography, Chip, Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import UserModal from './UserModal';

const RIGHT_LABEL = { admin: 'Admin', read: 'Lesen', write: 'Bearbeiten' };
const RIGHT_COLOR = { admin: 'secondary', write: 'primary', read: 'default' };

function RightChips({ rights }) {
    if (!rights || rights.length === 0) {
        return <Typography variant="caption" color="text.secondary">Keine</Typography>;
    }
    return (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {rights.map((r) => (
                <Chip
                    key={r}
                    size="small"
                    label={RIGHT_LABEL[r] || r}
                    color={RIGHT_COLOR[r] || 'default'}
                    variant="outlined"
                />
            ))}
        </Stack>
    );
}

export default function Users() {
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));
    const userModalRef = useRef(null);
    const [users, setUsers] = useState([]);
    const [error, setError] = useState(null);
    const [canAdmin, setCanAdmin] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [pwOpen, setPwOpen] = useState(false);
    const [pwValue, setPwValue] = useState('');
    const [pwError, setPwError] = useState('');
    const [pwSaving, setPwSaving] = useState(false);

    useEffect(() => {
        checkAdmin();
        loadUsers();
    }, []);

    async function checkAdmin() {
        const r = await API.hasRight('admin');
        setCanAdmin(!!r?.success);
    }

    async function loadUsers() {
        const result = await API.getUsers();
        if (result.success) setUsers(result.msg || []);
        else setError(result.msg);
    }

    function handleNew() {
        if (userModalRef.current) userModalRef.current.handleOpen(null);
    }

    function handleEdit(user) {
        if (userModalRef.current) userModalRef.current.handleOpen(user);
    }

    function openDelete(user) {
        setSelectedUser(user);
        setDeleteOpen(true);
    }

    async function confirmDelete() {
        if (!selectedUser?._id) return;
        const res = await API.deleteUser({ _id: selectedUser._id });
        if (!res?.success) alert('Fehler beim Löschen: ' + (res?.msg || 'Unbekannter Fehler'));
        setDeleteOpen(false);
        setSelectedUser(null);
        loadUsers();
    }

    function openPassword(user) {
        setSelectedUser(user);
        setPwValue('');
        setPwError('');
        setPwOpen(true);
    }

    async function submitPassword() {
        if (!pwValue) { setPwError('Passwort ist erforderlich.'); return; }
        setPwSaving(true);
        setPwError('');
        try {
            const res = await API.updateUser({ _id: selectedUser._id, password: pwValue });
            if (!res?.success) { setPwError(res?.msg || 'Fehlgeschlagen.'); return; }
            setPwOpen(false);
            setSelectedUser(null);
        } finally {
            setPwSaving(false);
        }
    }

    const rowActions = (user) => (
        <>
            {canAdmin && (
                <Tooltip title="Bearbeiten">
                    <IconButton size={mobile ? 'medium' : 'small'} onClick={() => handleEdit(user)}>
                        <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
            {canAdmin && (
                <Tooltip title="Passwort setzen">
                    <IconButton size={mobile ? 'medium' : 'small'} onClick={() => openPassword(user)}>
                        <KeyOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
            {canAdmin && (
                <Tooltip title="Löschen">
                    <IconButton size={mobile ? 'medium' : 'small'} color="error" onClick={() => openDelete(user)}>
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
        </>
    );

    return (
        <Box>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
                sx={{ mb: 3 }}
            >
                <Typography variant="h5" sx={{ fontWeight: 600 }}>Benutzerverwaltung</Typography>
                {canAdmin && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew}>
                        Neuer Benutzer
                    </Button>
                )}
            </Stack>

            {error ? (
                <Paper sx={{ p: 3 }}>
                    <Typography color="error">Fehler: {error}</Typography>
                </Paper>
            ) : mobile ? (
                <Stack spacing={1.5}>
                    {users.map((user) => (
                        <Paper key={user._id} sx={{ p: 2 }}>
                            <Typography sx={{ fontWeight: 600 }}>{user.name}</Typography>
                            {user.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                    {user.description}
                                </Typography>
                            )}
                            <Box sx={{ mt: 1.5 }}>
                                <RightChips rights={user.rights} />
                            </Box>
                            {canAdmin && (
                                <Stack
                                    direction="row"
                                    spacing={0.5}
                                    justifyContent="flex-end"
                                    sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
                                >
                                    {rowActions(user)}
                                </Stack>
                            )}
                        </Paper>
                    ))}
                    {users.length === 0 && (
                        <Paper sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">Keine Benutzer gefunden</Typography>
                        </Paper>
                    )}
                </Stack>
            ) : (
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Anzeigename</TableCell>
                                <TableCell>Berechtigungen</TableCell>
                                <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow key={user._id} hover>
                                    <TableCell>
                                        <Typography sx={{ fontWeight: 600 }}>{user.name}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {user.description || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <RightChips rights={user.rights} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                            {rowActions(user)}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {users.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary">Keine Benutzer gefunden</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <UserModal modalRef={userModalRef} onSaved={loadUsers} />

            <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
                <DialogTitle>Benutzer löschen?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Möchten Sie Benutzer <strong>{selectedUser?.name}</strong> wirklich löschen?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteOpen(false)}>Abbrechen</Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">Löschen</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={pwOpen} onClose={() => setPwOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Passwort setzen</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Neues Passwort für <strong>{selectedUser?.name}</strong>
                    </Typography>
                    <TextField
                        label="Neues Passwort"
                        type="password"
                        fullWidth
                        autoFocus
                        value={pwValue}
                        onChange={(e) => setPwValue(e.target.value)}
                        autoComplete="new-password"
                    />
                    {pwError && (
                        <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
                            {pwError}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setPwOpen(false)} disabled={pwSaving}>Abbrechen</Button>
                    <Button onClick={submitPassword} variant="contained" disabled={pwSaving}>
                        {pwSaving ? 'Speichert…' : 'Speichern'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
